/**
 * URL state for `/reports/message-log` (Order 31D).
 * Same Organization-timezone YYYY-MM-DD date semantics as Outgoing/Incoming.
 */
import {
  DEFAULT_REPORT_MESSAGE_PAGE_SIZE,
  DEFAULT_REPORT_MESSAGE_SORT,
  MESSAGE_DIRECTIONS,
  MESSAGE_ORIGINS,
  MESSAGE_STATUSES,
  MESSAGE_TYPES,
  PLATFORM_TYPES,
  REPORT_MESSAGE_PAGE_SIZES,
  REPORT_MESSAGE_SORTS,
  type MessageDirectionName,
  type MessageOriginName,
  type MessageStatusName,
  type MessageTypeName,
  type PlatformTypeName,
  type ReportMessagePageSize,
  type ReportMessageSortName,
} from "@multi-chat/shared";

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Origins that are currently meaningful product producers for Reports filters.
 * Future enum values remain in the shared domain but stay hidden in Message Log UX.
 */
export const MESSAGE_LOG_ACTIVE_ORIGINS = [
  "INBOX",
  "CAMPAIGN",
  "CAMPAIGN_SEND_TEST",
  "SYSTEM",
] as const satisfies readonly MessageOriginName[];

export type MessageLogDirectionFilter = "ALL" | "INBOUND" | "OUTBOUND";

export type MessageLogQuery = {
  from?: string;
  to?: string;
  search: string;
  /** Undefined ⇒ no direction filter (combined dataset / ALL). */
  direction?: MessageDirectionName;
  platform?: PlatformTypeName;
  endpointId?: number;
  messageType?: MessageTypeName;
  status?: MessageStatusName;
  origin?: MessageOriginName;
  campaignId?: number;
  template?: string;
  page: number;
  pageSize: ReportMessagePageSize;
  sort: ReportMessageSortName;
};

function parseYmd(raw: string | null): string | null {
  if (raw == null || !YMD_RE.test(raw)) return null;
  const parts = raw.split("-").map(Number);
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (y == null || m == null || d == null) return null;
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  return raw;
}

function parsePositiveInt(raw: string | null): number | undefined {
  if (raw == null || raw.trim() === "") return undefined;
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed < 1) return undefined;
  return parsed;
}

function isPlatform(value: string | null): value is PlatformTypeName {
  return value != null && (PLATFORM_TYPES as readonly string[]).includes(value);
}

function isDirection(value: string | null): value is MessageDirectionName {
  return value != null && (MESSAGE_DIRECTIONS as readonly string[]).includes(value);
}

function isOrigin(value: string | null): value is MessageOriginName {
  return value != null && (MESSAGE_ORIGINS as readonly string[]).includes(value);
}

function isStatus(value: string | null): value is MessageStatusName {
  return value != null && (MESSAGE_STATUSES as readonly string[]).includes(value);
}

function isMessageType(value: string | null): value is MessageTypeName {
  return value != null && (MESSAGE_TYPES as readonly string[]).includes(value);
}

function isSort(value: string | null): value is ReportMessageSortName {
  return value != null && (REPORT_MESSAGE_SORTS as readonly string[]).includes(value);
}

export function parseMessageLogQuery(search: string): MessageLogQuery {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const from = parseYmd(params.get("from"));
  const to = parseYmd(params.get("to"));
  const pageSizeRaw = Number(params.get("pageSize"));
  const pageSize = (REPORT_MESSAGE_PAGE_SIZES as readonly number[]).includes(pageSizeRaw)
    ? (pageSizeRaw as ReportMessagePageSize)
    : DEFAULT_REPORT_MESSAGE_PAGE_SIZE;
  const result: MessageLogQuery = {
    search: (params.get("search") ?? "").trim().slice(0, 200),
    page: parsePositiveInt(params.get("page")) ?? 1,
    pageSize,
    sort: isSort(params.get("sort"))
      ? (params.get("sort") as ReportMessageSortName)
      : DEFAULT_REPORT_MESSAGE_SORT,
  };
  if (from != null && to != null) {
    result.from = from <= to ? from : to;
    result.to = from <= to ? to : from;
  }
  const directionRaw = params.get("direction");
  if (isDirection(directionRaw)) result.direction = directionRaw;
  const platformRaw = params.get("platform");
  if (isPlatform(platformRaw)) result.platform = platformRaw;
  const endpointId = parsePositiveInt(params.get("endpointId"));
  if (endpointId != null) result.endpointId = endpointId;
  const messageTypeRaw = params.get("messageType");
  if (isMessageType(messageTypeRaw)) result.messageType = messageTypeRaw;
  const statusRaw = params.get("status");
  if (isStatus(statusRaw)) result.status = statusRaw;
  const originRaw = params.get("origin");
  if (isOrigin(originRaw)) result.origin = originRaw;
  const campaignId = parsePositiveInt(params.get("campaignId"));
  if (campaignId != null) result.campaignId = campaignId;
  const templateRaw = params.get("template")?.trim();
  if (templateRaw) result.template = templateRaw.slice(0, 200);
  return result;
}

export function messageLogHref(query: MessageLogQuery): string {
  const params = new URLSearchParams();
  if (query.from && query.to) {
    params.set("from", query.from);
    params.set("to", query.to);
  }
  if (query.search.trim().length > 0) params.set("search", query.search.trim());
  if (query.direction) params.set("direction", query.direction);
  if (query.platform) params.set("platform", query.platform);
  if (query.endpointId != null) params.set("endpointId", String(query.endpointId));
  if (query.messageType) params.set("messageType", query.messageType);
  if (query.status) params.set("status", query.status);
  if (query.origin) params.set("origin", query.origin);
  if (query.campaignId != null) params.set("campaignId", String(query.campaignId));
  if (query.template?.trim()) params.set("template", query.template.trim());
  if (query.page > 1) params.set("page", String(query.page));
  if (query.pageSize !== DEFAULT_REPORT_MESSAGE_PAGE_SIZE) {
    params.set("pageSize", String(query.pageSize));
  }
  if (query.sort !== DEFAULT_REPORT_MESSAGE_SORT) params.set("sort", query.sort);
  const qs = params.toString();
  return qs.length > 0 ? `/reports/message-log?${qs}` : "/reports/message-log";
}

export function messageLogDirectionFilter(query: MessageLogQuery): MessageLogDirectionFilter {
  return query.direction ?? "ALL";
}

/** Outbound-oriented filters that conflict with Direction=INCOMING. */
export function hasOutboundOnlyMessageLogFilters(query: MessageLogQuery): boolean {
  if (query.origin) return true;
  if (query.campaignId != null) return true;
  if (query.template?.trim()) return true;
  return false;
}

export function hasActiveMessageLogFilters(query: MessageLogQuery): boolean {
  if (query.search.trim().length > 0) return true;
  if (query.from != null || query.to != null) return true;
  if (query.direction) return true;
  if (query.platform) return true;
  if (query.endpointId != null) return true;
  if (query.messageType) return true;
  if (query.status) return true;
  if (query.origin) return true;
  if (query.campaignId != null) return true;
  if (query.template?.trim()) return true;
  return false;
}

export function clearMessageLogFilters(query: MessageLogQuery): MessageLogQuery {
  return {
    search: "",
    page: 1,
    pageSize: query.pageSize,
    sort: query.sort,
  };
}

/** When switching to Incoming, drop outbound-only filters once (explicit Direction change). */
export function clearOutboundOnlyFilters(query: MessageLogQuery): MessageLogQuery {
  const next: MessageLogQuery = { ...query, page: 1 };
  delete next.origin;
  delete next.campaignId;
  delete next.template;
  return next;
}
